import '@atlaskit/css-reset';
import React from 'react';
import { FlagsProvider } from '@atlaskit/flag/flag-provider';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Suspense from '@atlaskit/react-ufo/suspense';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import HeartbeatDisplay from '@atlassian/heartbeat/heartbeat';
import { RelayEnvironmentProvider } from 'react-relay';
import { AkAnalyticsListeners } from '@townsquare/analytics/ak-listener';
import { AnalyticsClientImpl } from '@townsquare/analytics/client';
import { AnalyticsContext } from '@townsquare/analytics/context';
import { useAtlasKitAnalytics } from '@townsquare/analytics/use-ak-analytics';
import { AppContext } from '@townsquare/app-context';
import { useLoadAppsFromResource } from '@townsquare/apps-store/hooks';
import { AuthCheck } from '@townsquare/auth';
import { StorageAuthCheck } from '@townsquare/auth/storage-auth-check';
import { returnAfterLogin } from '@townsquare/auth-utils';
import { useBrowserStorageControls } from '@townsquare/auth-utils/use-browser-storage-controls';
import { useRovoWorkspace } from '@townsquare/collab-context';
import { CrossFlowProvider } from '@townsquare/crossflow/provider';
import { CrossFlowWAC } from '@townsquare/crossflow/wac';
import { Drawers } from '@townsquare/drawers';
import { setFavicon } from '@townsquare/dynamic-favicon';
import { Flags } from '@townsquare/flags/wrapper';
import { HelpContextProvider } from '@townsquare/help-context';
import { UserLocaleOverrideHandler } from '@townsquare/i18n/override';
import TownsquareIntlProvider from '@townsquare/i18n/provider';
import { Modals } from '@townsquare/modals/ui';
import { RouteListener } from '@townsquare/performance/route-listener';
import { PostOfficeContextProvider } from '@townsquare/post-office/post-office-provider';
import { TownsquarePreviewPanelsProvider } from '@townsquare/preview-panels/townsquarePreviewPanelsProvider';
import { initialiseAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { getRelayEnvironment } from '@townsquare/relay-utils/townsquare-environment';
import { RouteBlocker } from '@townsquare/route-blocker';
import { CapabilityRouter } from '@townsquare/router/capability-router';
import { createBrowserHistory } from '@townsquare/router/createBrowserHistory';
import { isEmbedRoute, matchRoute } from '@townsquare/router/primitives/match-route';
import { useRouteListeners } from '@townsquare/router/use-route-listeners';
import { useQueryParam } from '@townsquare/router/useQueryParam';
import { RovoChatSubscribers } from '@townsquare/rovo-chat/subscribers';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { expValWithoutExposure } from '@townsquare/stat-sig';
import { ROVO_CHAT_NON_MAU_TOWNSQUARE_CTA } from '@townsquare/stat-sig/experiments';
import { fg } from '@townsquare/stat-sig/gate';
import { GlobalTheming } from '@townsquare/theme/global-theming';
import { AnalyticAttributeHandler } from './AnalyticsAttributeHandler';
import { TeamsNavigationProviderWrapper } from './TeamsNavigationProviderWrapper';
import { Bootstrap, BootstrapResult } from './bootstrap/Bootstrap';
import { useKeyboardShortcuts } from './griffin-navigation/use-keyboard-shortcuts';
import { EmbedRoute } from './router/EmbedRoute';
import { GriffinRoute } from './router/GriffinRoute';
import { useTeamsRoutes, useRoute } from './router/hooks';
import { routes } from './router/routes';
const analyticsClient = new AnalyticsClientImpl('web', 'townsquare');
const history = createBrowserHistory();
setFavicon('teams');
const EnforceCloudIdUrlParams = (props: {
  cloudId?: string;
}) => {
  const route = useRoute();
  const rovoWorkspaceCloudId = useRovoWorkspace();
  const cloudId = props.cloudId ?? rovoWorkspaceCloudId;
  const [qsCloudId, setQsCloudId] = useQueryParam('cloudId');
  if ('routeType' in route && route.routeType === 'global' && qsCloudId !== cloudId) {
    setQsCloudId(cloudId ?? undefined, 'replace');
  }
  return null;
};
const InsideBootstrap = (data: BootstrapResult) => {
  // Compliance control
  useBrowserStorageControls();
  const teamsRoutes = useTeamsRoutes();
  const akAnalytics = useAtlasKitAnalytics();
  const isUGSHeadersMigrationEnabled = fg('townsquare_ugs_headers_migration');
  const isCleanUpTeamsRovoSkillEnabled = fg('enable_clean_up_teams_agentic_skill');
  const isRovoChatNonMauEnabled = expValWithoutExposure(ROVO_CHAT_NON_MAU_TOWNSQUARE_CTA, 'rovo_chat_non_mau_townsquare_cta_treatment', 'control') !== 'control';
  if (isUGSHeadersMigrationEnabled) {
    initialiseAggRelayEnvironment(data.collabContext?.rovoWorkspace ?? undefined);
  }
  const cloudId = data.collabContext?.rovoWorkspace || data.workspace?.cloudId;
  const forceNonSharded = !data.workspace;
  useKeyboardShortcuts();
  useLoadAppsFromResource();
  return data.collabContext?.rovoWorkspace ? <RelayEnvironmentProvider environment={getRelayEnvironment(cloudId, forceNonSharded)}>
			<CrossFlowProvider>
				<AkAnalyticsListeners client={akAnalytics}>
					<EnforceCloudIdUrlParams cloudId={cloudId} />
					<AnalyticAttributeHandler />
					<UserLocaleOverrideHandler />
					{(isCleanUpTeamsRovoSkillEnabled || isRovoChatNonMauEnabled) && <RovoChatSubscribers />}
					<GlobalTheming />
					<HeartbeatDisplay onAuthenticationFailed={returnAfterLogin} />
					<CapabilityRouter history={history} resourceContext={data} data={data.relayKey} routes={teamsRoutes}>
						<TownsquarePreviewPanelsProvider featureGate="townsquare_teams_preview_panel_enabled">
							<TeamsNavigationProviderWrapper cloudId={cloudId ?? ''}>
								<Suspense interactionName="teams-route-switcher" fallback={<FullScreenSpaLoader />}>
									<RouteListener />
									<RouteSwitcher />
								</Suspense>
							</TeamsNavigationProviderWrapper>
						</TownsquarePreviewPanelsProvider>
					</CapabilityRouter>
					<Modals />
					<Drawers />
					<Flags />
					<RouteBlocker />
				</AkAnalyticsListeners>
			</CrossFlowProvider>
		</RelayEnvironmentProvider> : <div />;
};
export function App(): React.JSX.Element {
  const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
  const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);
  const AuthenticationCheck = isEmbedRoute(matchedRoute?.route) ? StorageAuthCheck : AuthCheck;
  return <UFOSegment name="teams-app-root">
			<AuthenticationCheck>
				<AppContext.Provider value={{
        app: 'teams'
      }}>
					<TownsquareIntlProvider app="teams">
						<RelayEnvironmentProvider environment={getRelayEnvironment()}>
							<AnalyticsContext.Provider value={analyticsClient}>
								<FlagsProvider>
									<PostOfficeContextProvider>
										<HelpContextProvider>
											<ModalContextProvider>
												<Bootstrap history={history}>
													{data => <InsideBootstrap {...data} />}
												</Bootstrap>
											</ModalContextProvider>
										</HelpContextProvider>
									</PostOfficeContextProvider>
								</FlagsProvider>
								{/* WAC via Start Cross Flow touch point support */}
								<CrossFlowWAC />
							</AnalyticsContext.Provider>
						</RelayEnvironmentProvider>
					</TownsquareIntlProvider>
				</AppContext.Provider>
			</AuthenticationCheck>
		</UFOSegment>;
}
function RouteSwitcher() {
  const {
    embed
  } = useRoute();
  useRouteListeners(); // Required to init analytics
  return embed ? <EmbedRoute /> : <GriffinRoute />;
}
export default App;